import { FC } from 'react'

import { Alert, Box, Text } from '@factorialco/deprecated-design-system'
import { ExpensesExpensableFilter } from 'generated/resources/datatable.graphql'
import {
  ExpensablesPendingApprovalWidgetDocument,
  ExpensablesPendingApprovalWidgetQuery,
  ExpensesEntitiesExpensableStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { removeNullBetween } from 'modules/finance/components/Widgets/common/utils'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number, currency: string) => string
  forcedCurrency: string
}

interface QueryArgsType {
  from?: string
  to?: string
  employeeIds?: number[]
  groupIds?: number[]
  statuses: ExpensesEntitiesExpensableStatusEnum[]
}

const prepareData = (
  data: ExpensablesPendingApprovalWidgetQuery,
  prepareDataArgs: PreparedDataArgsType
) => {
  const hasMultipleCurrencies =
    (data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.values?.length || 0) > 1
  const currency =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.values?.[0] ||
    prepareDataArgs.forcedCurrency
  const totalAmount =
    data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.sum?.amount?.[0] || 0
  const totalCount = data?.expenses?.expensablesConnection?.aggregate?.terms?.currency?.count || 0

  return {
    countersChartDatas: [
      {
        value: totalAmount,
        formattedValue: prepareDataArgs.amountFormatter(totalAmount, currency),
        label: '',
      },
    ],
    hasMultipleCurrencies,
    totalCount,
  }
}

export const buildExpensablesPendingApprovalWidget = ({
  widgetTitle,
  queryArgs,
  prepareDataArgs,
  filters,
}: {
  widgetTitle?: string
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
  filters: ExpensesExpensableFilter
}) => {
  const ExpensesPendingApprovalWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { countersChartDatas, hasMultipleCurrencies, totalCount } = data

    if (hasMultipleCurrencies) {
      return (
        <Alert color="note">{i18n.t('expenses_v2.list.insights.empty_case.description')}</Alert>
      )
    }

    return (
      <FinanceWidget
        id="expenses-pending-approval-widget"
        title={widgetTitle}
        type={SupportedCharts.COUNTERS}
        showChart={false}
        aspect="wide"
        countersChartDatas={countersChartDatas.map((chartData) => ({
          ...chartData,
        }))}
      >
        <Box>
          <Text size="200">
            {i18n.t('expenses_v2.list.insights.labels.expenses_count', {
              count: totalCount,
            })}
          </Text>
        </Box>
      </FinanceWidget>
    )
  }

  const filteredFilters = removeNullBetween(filters)

  return buildWidget({
    id: 'expensables-pending-approval-widget',
    component: ExpensesPendingApprovalWidget,
    document: ExpensablesPendingApprovalWidgetDocument,
    prepareData: (data: ExpensablesPendingApprovalWidgetQuery) =>
      prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
      filter: {
        ...filteredFilters,
      },
    },
  })
}
